import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Check, ArrowLeft, Crown, Sparkles, Zap, ShieldCheck,
  Info, Star, Plus, Minus,
  ArrowRight, X, CheckCircle2, MessageSquare, TrendingDown, User as UserIcon, LogOut
} from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import CashfreeCheckoutButton from './CashfreeCheckoutButton';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import FeedbackHeaderCTA from './FeedbackHeaderCTA';
import ThemeToggle from './ThemeToggle';
import { getProfile, verifyCashfreeOrder } from '../api/insights';

const InfoTooltip = ({ text, isDark }) => {
  const [show, setShow] = React.useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 4, cursor: 'help' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <Info size={13} style={{ opacity: 0.6 }} />
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 8px)',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 200,
              padding: '8px 12px',
              background: isDark ? '#1e293b' : '#ffffff',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
              borderRadius: 8,
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
              color: isDark ? 'rgba(255,255,255,0.9)' : '#1e293b',
              fontSize: 11,
              lineHeight: 1.4,
              zIndex: 100,
              pointerEvents: 'none',
              textAlign: 'center'
            }}
          >
            {text}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AccordionItem = ({ question, answer, isOpen, onClick, accentColor, textPrimary, textSecondary, borderColor, isLast }) => {
  return (
    <div style={{
      borderBottom: isLast ? 'none' : `1px solid ${borderColor}`,
      padding: '18px 0',
    }}>
      <button
        type="button"
        onClick={onClick}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          textAlign: 'left',
          color: textPrimary,
          gap: 12,
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 600, paddingRight: 12, lineHeight: 1.35 }}>{question}</span>
        {isOpen ? <Minus size={18} color={accentColor} strokeWidth={2.25} /> : <Plus size={18} color="#94a3b8" strokeWidth={2.25} />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <p style={{
              paddingTop: 12,
              fontSize: 14,
              lineHeight: 1.62,
              color: textSecondary,
            }}>
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AnsweringDrawer = ({ isOpen, onClose, isDark }) => {
  const [openIndex, setOpenIndex] = React.useState(null);

  const drawerData = [
    {
      q: "Tired of cold-applying and never hearing back?",
      a: "FairHire surfaces past colleagues already inside your target company, ranked by referral strength — so you stop spraying applications and start having conversations."
    },
    {
      q: "Don't know who to message inside a target company?",
      a: "We pinpoint the best person to contact based on overlap with your career path, mutual context, and likelihood-to-respond — no more guessing on LinkedIn."
    },
    {
      q: "Not sure which companies actually hire your background?",
      a: "We analyse 320K+ verified career paths and show you the top companies where people just like you have been hired in the last 12 months."
    },
    {
      q: "Wondering if a JD truly matches your career path?",
      a: "Instead of trusting buzzword-stuffed JDs, we show you how people with backgrounds like yours actually moved into similar roles — the real prerequisites, not the wishlist."
    },
    {
      q: "Unable to find friends' companies with matching roles?",
      a: "FairHire cross-checks every open role inside your network's companies against your profile, so a matching opportunity at a friend's company never slips by."
    },
    {
      q: "Struggling to find someone who can actually refer you?",
      a: "Each potential referrer is scored on tenure overlap with you, current seniority, and historic referral activity — top of the list = most likely to actually help."
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: 'fixed', inset: 0, zIndex: 1000,
              background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)'
            }}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            style={{
              position: 'fixed', right: 0, top: 0, bottom: 0, width: '100%', maxWidth: 450,
              background: isDark ? '#0d1019' : '#ffffff',
              zIndex: 1001, boxShadow: '-10px 0 30px rgba(0,0,0,0.1)',
              padding: '40px 32px', overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 40 }}>
              <div>
                <p style={{ fontSize: 11, fontWeight: 800, color: isDark ? '#F59E0B' : '#2563eb', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>HOW FAIRHIRE HELPS</p>
                <h3 style={{ fontSize: 24, fontWeight: 800, color: isDark ? 'rgba(255,255,255,0.9)' : '#0f172a' }}>Common questions, real answers</h3>
              </div>
              <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: isDark ? 'rgba(255,255,255,0.82)' : '#0f172a' }}>
                <X size={24} />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {drawerData.map((item, i) => (
                <div key={i} style={{
                  background: openIndex === i ? (isDark ? 'rgba(245,158,11,0.08)' : '#f8fafc') : (isDark ? 'rgba(255,255,255,0.03)' : '#ffffff'),
                  padding: 20, borderRadius: 16, border: `1px solid ${openIndex === i ? (isDark ? 'rgba(245,158,11,0.45)' : '#2563eb') : (isDark ? 'rgba(255,255,255,0.1)' : '#f1f5f9')}`,
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }}
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', background: openIndex === i ? (isDark ? '#F59E0B' : '#2563eb') : (isDark ? 'rgba(255,255,255,0.1)' : '#f1f5f9'),
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: openIndex === i ? '#fff' : (isDark ? 'rgba(255,255,255,0.55)' : '#94a3b8'), fontSize: 12, fontWeight: 800
                      }}>
                        {i + 1}
                      </div>
                      <h4 style={{ fontSize: 15, fontWeight: 700, color: isDark ? 'rgba(255,255,255,0.9)' : '#0f172a' }}>{item.q}</h4>
                    </div>
                    {openIndex === i ? <Minus size={18} color={isDark ? '#F59E0B' : '#2563eb'} /> : <Plus size={18} color={isDark ? 'rgba(255,255,255,0.55)' : '#94a3b8'} />}
                  </div>
                  <AnimatePresence>
                    {openIndex === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <p style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6, color: isDark ? 'rgba(255,255,255,0.7)' : '#475569' }}>{item.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 40, textAlign: 'center' }}>
              <p style={{ fontSize: 12, color: isDark ? 'rgba(255,255,255,0.5)' : '#94a3b8' }}>Click any question to see how FairHire helps.</p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const PricingPage = () => {
  const { isDark } = useTheme();
  const reduceMotion = useReducedMotion();
  const { user, login, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [openFaqIndex, setOpenFaqIndex] = React.useState(null); // All closed by default
  const [isProfileMenuOpen, setIsProfileMenuOpen] = React.useState(false);

  const returnState = location.state?.returnState || null;
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/insights-list', returnState ? { state: returnState } : undefined);
  };
  const handlePaymentSuccess = React.useCallback(async () => {
    try {
      const data = await getProfile();
      const profileLike = (data?.profile && typeof data.profile === 'object') ? data.profile : (data || {});
      const token = localStorage.getItem('insights_token');
      if (profileLike && token) {
        login(profileLike, token);
      }
    } catch (_) {
      // Ignore refresh failures; premium state will sync on next fetch.
    }
  }, [login]);
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('cashfree_order_id');
    const status = (params.get('cashfree_status') || '').toUpperCase();
    if (!orderId) return;

    let cancelled = false;
    (async () => {
      try {
        const verification = await verifyCashfreeOrder(orderId);
        if (cancelled) return;
        if (verification?.success || status === 'PAID') {
          toast.success('Payment successful. Premium unlocked.');
          await handlePaymentSuccess();
        } else {
          toast.error('Payment not completed. Please try again.');
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err.message || 'Unable to verify payment');
        }
      } finally {
        const cleaned = new URLSearchParams(window.location.search);
        cleaned.delete('cashfree_order_id');
        cleaned.delete('cashfree_status');
        const next = cleaned.toString();
        navigate(next ? `/pricing?${next}` : '/pricing', { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, handlePaymentSuccess]);
  const appCls = isDark ? 'dark-app' : 'light-app';
  const accentColor = isDark ? '#F59E0B' : '#2563eb';
  const accentBg = isDark ? 'linear-gradient(135deg,#F59E0B,#D97706)' : 'linear-gradient(135deg,#3b82f6,#2563eb)';
  const bgPage = isDark ? 'rgba(13,16,25,0.92)' : '#EEF2F8';
  const headerBg = isDark ? 'rgba(13,16,25,0.92)' : '#F3F6FB';
  const borderSub = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(148,163,184,0.28)';
  const theme = {
    bg: bgPage,
    textPrimary: isDark ? 'rgba(255,255,255,0.9)' : '#0f172a',
    textSecondary: isDark ? 'rgba(255,255,255,0.7)' : '#475569',
    textMuted: isDark ? 'rgba(255,255,255,0.5)' : '#64748b',
    border: borderSub,
    cardBg: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
    cardBorder: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
    accent: accentColor,
    accentGradient: accentBg,
  };

  const freeItems = [
    "See top 10 companies per insights block",
    "Find the best person to contact inside a company (up to 3 companies)",
    "Limited referral network across all past companies and colleges",
    "Find companies where people like you get hired, and discover matching roles inside them."
  ];

  const premiumUnlockedItems = [
    "See all companies per insight, not just the top 3",
    "Find the best person to contact with no limit on companies",
    "Referral network built from your entire professional DNA",
    "Priority access to every new insight and feature"
  ];

  const tickerItems = [
    'Tired of cold-applying and never hearing back?',
    'Don\'t know who to message inside a target company?',
    'Not sure which companies actually hire your background?',
    'Wondering if a JD truly matches your career path?',
    'Unable to find friends\' companies with matching roles?',
    'Struggling to find someone who can actually refer you?'
  ];
  const PREMIUM_PRICE = 349;

  const faqItems = [
    {
      q: "How is FairHire different from LinkedIn search?",
      a: "LinkedIn shows you who works where. FairHire shows you where people like you actually got hired, identifies the people most likely to refer you based on shared career paths and network strength, and ranks them by real referral potential so you stop guessing."
    },
    {
      q: "Is the ₹349 a subscription?",
      a: "No. It's a one-time payment that unlocks premium insights for your current job search. No recurring charges, no hidden fees."
    },
    {
      q: "Why should I trust the company list you show me?",
      a: "Every company in your list is derived from people with backgrounds genuinely similar to yours who were recently hired."
    },
    {
      q: "What if my friends aren't on the platform?",
      a: "FairHire isn't just about your existing friends. It's about mapping your 'professional DNA' to the thousands of people in your extended alumni network who you don't know yet, but who share a bond with you."
    },
    {
      q: "Can I get a refund if it doesn't help me?",
      a: "Yes. If you don't find the insights helpful within 7 days, just email us at support@fairhire.in and we'll refund your payment, no questions asked."
    }
  ];


  const easeOut = [0.22, 1, 0.36, 1];
  const motionDur = reduceMotion ? 0.01 : 0.42;
  const motionStagger = reduceMotion ? 0 : 0.08;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: motionStagger, delayChildren: reduceMotion ? 0 : 0.04 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 14 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: motionDur, ease: easeOut },
    },
  };
  const CONTENT_MAX_WIDTH = 1180;
  const pxInset = { l: 'max(24px, env(safe-area-inset-left))', r: 'max(24px, env(safe-area-inset-right))' };

  return (
    <div className={`${appCls} pricing-page-root`} style={{
      minHeight: '100vh',
      background: theme.bg,
      color: theme.textPrimary,
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      position: 'relative',
      WebkitFontSmoothing: 'antialiased',
    }}>
      <AnsweringDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} isDark={isDark} />

      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backdropFilter: 'saturate(1.2) blur(14px)',
        WebkitBackdropFilter: 'saturate(1.2) blur(14px)',
        background: isDark ? headerBg : 'rgba(243, 246, 251, 0.88)',
        borderBottom: `1px solid ${borderSub}`,
        paddingLeft: pxInset.l,
        paddingRight: pxInset.r,
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            type="button"
            onClick={handleBack}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'none', border: 'none', cursor: 'pointer',
              color: isDark ? 'rgba(255,255,255,0.6)' : '#475569', fontSize: 13
            }}
            onMouseEnter={e => e.currentTarget.style.color = isDark ? '#fff' : '#0f172a'}
            onMouseLeave={e => e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.6)' : '#475569'}
          >
            <ArrowLeft size={15} />
            <span>Back</span>
          </button>
          <div style={{ width: 1, height: 20, background: isDark ? 'rgba(255,255,255,0.12)' : '#cbd5e1' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <div style={{
              width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#fff', borderRadius: '6px', padding: '4px'
            }}>
              <img src="/assets/logo.png" alt="fairhire logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <span style={{ fontSize: 16, fontWeight: 800, fontFamily: "'Playfair Display', serif", color: isDark ? '#fff' : '#0f172a' }}>fairhire</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ThemeToggle />
          <FeedbackHeaderCTA />
          <div
            style={{ position: 'relative', marginLeft: 2 }}
            onMouseEnter={() => setIsProfileMenuOpen(true)}
            onMouseLeave={() => setIsProfileMenuOpen(false)}
          >
            <button
              type="button"
              onClick={() => setIsProfileMenuOpen(v => !v)}
              aria-label="Open profile menu"
              data-testid="profile-icon"
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(37,99,235,0.1)',
                border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(37,99,235,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <UserIcon size={16} color={isDark ? 'rgba(255,255,255,0.8)' : '#2563eb'} />
            </button>
            <AnimatePresence>
              {isProfileMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.16 }}
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 'calc(100% + 10px)',
                    width: 200,
                    background: isDark ? 'rgba(13,16,25,0.98)' : '#ffffff',
                    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #dbe7fb',
                    borderRadius: 14,
                    padding: 8,
                    boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.6)' : '0 12px 36px rgba(37,99,235,0.18)',
                    zIndex: 80,
                  }}
                >
                  <div style={{ padding: '8px 12px', borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0', marginBottom: 4 }}>
                    <p style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b', marginBottom: 2 }}>Signed in as</p>
                    <p style={{ fontSize: 13, fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.9)' : '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user?.name || user?.email || 'User'}
                    </p>
                  </div>
                  <button
                    onClick={() => { setIsProfileMenuOpen(false); navigate('/profile'); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'none', border: 'none', color: isDark ? 'rgba(255,255,255,0.75)' : '#475569', fontSize: 13, cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(37,99,235,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                  >
                    <UserIcon size={14} />
                    Profile
                  </button>
                  <button
                    onClick={() => { logout(); setIsProfileMenuOpen(false); navigate('/'); toast.success('Logged out'); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'none', border: 'none', color: '#f87171', fontSize: 13, cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(248,113,113,0.12)' : 'rgba(248,113,113,0.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                  >
                    <LogOut size={14} />
                    Logout
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{
        maxWidth: CONTENT_MAX_WIDTH,
        margin: '0 auto',
        padding: `40px ${pxInset.r} 28px ${pxInset.l}`,
        textAlign: 'center',
      }}>
        <motion.div
          initial={{ opacity: 0, y: reduceMotion ? 0 : 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0.01 : 0.5, ease: easeOut }}
        >
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 16px',
            borderRadius: 999,
            background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.07)',
            border: `1px solid ${isDark ? 'rgba(245,158,11,0.3)' : 'rgba(37,99,235,0.18)'}`,
            marginBottom: 22,
          }}>
            <Sparkles size={14} color={accentColor} strokeWidth={2} />
            <span style={{ fontSize: 11, fontWeight: 800, color: accentColor, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Find where you fit</span>
          </div>
          <h1 style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: 'clamp(34px, 5.5vw, 52px)',
            fontWeight: 500,
            letterSpacing: '-0.03em',
            margin: '0 0 14px',
            lineHeight: 1.08,
            color: theme.textPrimary,
          }}>
            Stop applying <span style={{ color: accentColor, fontWeight: 700 }}>blind</span>.
          </h1>
          <p style={{
            color: theme.textSecondary,
            fontSize: 'clamp(16px, 2.2vw, 18px)',
            maxWidth: 560,
            margin: '0 auto',
            lineHeight: 1.58,
          }}>
            FairHire shows where people like you get hired and where you have the strongest referral chances.
          </p>

          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 2 }}>
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} size={14} fill="#ca8a04" color="#ca8a04" />
              ))}
            </div>
            <span style={{ fontSize: 13, color: theme.textMuted, fontWeight: 500, letterSpacing: '0.01em' }}>
              Loved by 400+ job seekers and growing
            </span>
          </div>
        </motion.div>
      </section>

      {/* Pain Points Ticker */}
      <section style={{ maxWidth: CONTENT_MAX_WIDTH, margin: '0 auto', padding: `8px ${pxInset.r} 8px ${pxInset.l}` }}>
        <div className="pricing-ticker-wrap" style={{ position: 'relative', overflow: 'hidden', padding: '12px 0 4px' }}>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
            <div style={{ display: 'flex', overflow: 'hidden', width: '100%', maskImage: 'linear-gradient(90deg, transparent 0%, black 4%, black 96%, transparent 100%)', WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 4%, black 96%, transparent 100%)' }}>
              {reduceMotion ? (
                <div className="pricing-ticker-static" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10, padding: '8px 0' }}>
                  {tickerItems.map((item, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: theme.cardBg,
                      padding: '12px 18px', borderRadius: 999, border: `1px solid ${theme.border}`,
                      boxShadow: isDark ? 'none' : '0 1px 8px rgba(15,23,42,0.04)',
                    }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: accentColor, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: theme.textSecondary, lineHeight: 1.35 }}>{item}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <motion.div
                  animate={{ x: [0, -1200] }}
                  transition={{ duration: 50, repeat: Infinity, ease: 'linear' }}
                  style={{ display: 'flex', gap: 16, whiteSpace: 'nowrap', padding: '8px 0', willChange: 'transform' }}
                >
                  {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: theme.cardBg,
                      padding: '12px 22px', borderRadius: 999, border: `1px solid ${theme.border}`,
                      boxShadow: isDark ? 'none' : '0 1px 8px rgba(15,23,42,0.04)',
                    }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: accentColor, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: theme.textSecondary }}>{item}</span>
                    </div>
                  ))}
                </motion.div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsDrawerOpen(true)}
              style={{
                background: 'none', border: 'none', color: theme.textSecondary, fontSize: 14, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', transition: 'color 0.2s ease, opacity 0.2s ease',
                opacity: 0.88, padding: '6px 10px', borderRadius: 10,
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = theme.textPrimary; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.color = theme.textSecondary; }}
            >
              <MessageSquare size={17} style={{ opacity: 0.75, flexShrink: 0 }} />
              <span>See how FairHire answers each</span>
              <ArrowRight size={17} style={{ opacity: 0.75, flexShrink: 0 }} />
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section style={{ maxWidth: CONTENT_MAX_WIDTH, margin: '0 auto', padding: `32px ${pxInset.r} 60px ${pxInset.l}` }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
            gap: 24,
            alignItems: 'stretch',
          }}
        >
          {/* Free Card */}
          <motion.div
            variants={itemVariants}
            whileHover={reduceMotion ? undefined : { y: -3 }}
            transition={{ type: 'spring', stiffness: 420, damping: 28 }}
            style={{
              background: theme.cardBg,
              padding: 'clamp(32px, 4vw, 44px)',
              borderRadius: 28,
              border: theme.cardBorder,
              boxShadow: isDark ? 'none' : '0 8px 28px rgba(15, 23, 42, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: isDark ? '#1e293b' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textMuted }}>
                  <Zap size={20} strokeWidth={2} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 800, color: theme.textMuted, letterSpacing: '0.12em' }}>FREE</span>
              </div>
              <span style={{ fontSize: 10, fontWeight: 800, color: theme.textMuted, letterSpacing: '0.14em' }}>FOREVER</span>
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 46, fontWeight: 900, color: theme.textPrimary, letterSpacing: '-0.03em' }}>₹0</span>
                <span style={{ fontSize: 15, color: theme.textMuted, fontWeight: 600 }}>/ forever</span>
              </div>
            </div>
            <p style={{ color: theme.textSecondary, fontSize: 15, marginBottom: 36, lineHeight: 1.58 }}>
              Find the right companies to target — for free, forever.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 40, flex: 1 }}>
              {freeItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ marginTop: 3, flexShrink: 0 }}><Check size={16} color={accentColor} strokeWidth={2.5} /></div>
                  <span style={{ fontSize: 14, color: theme.textSecondary, fontWeight: 500, lineHeight: 1.45 }}>{item}</span>
                </div>
              ))}
            </div>

            <button
              type="button"
              disabled
              style={{
                width: '100%', height: 56, borderRadius: 999,
                background: isDark ? '#1e293b' : '#f8fafc',
                border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                color: accentColor,
                fontWeight: 800, fontSize: 15, cursor: 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: accentColor }} />
              {user?.is_premium ? 'Included in Premium' : 'Current plan'}
            </button>
          </motion.div>

          {/* Premium Card — hex colors only: .light-app CSS matches rgba(255,255,255,…) in style="" */}
          <motion.div
            variants={itemVariants}
            whileHover={reduceMotion ? undefined : { y: -3 }}
            transition={{ type: 'spring', stiffness: 420, damping: 28 }}
            className="pricing-premium-card"
            style={{
              background: isDark ? '#111827' : '#1e293b',
              padding: 'clamp(32px, 4vw, 44px)',
              borderRadius: 28,
              border: '1px solid #475569',
              boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.35)' : '0 20px 50px rgba(15, 23, 42, 0.18)',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              height: '100%',
            }}
          >
            <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
              <div style={{
                background: accentColor,
                color: '#ffffff',
                padding: '8px 20px',
                borderRadius: 10,
                fontSize: 11,
                fontWeight: 900,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                boxShadow: '0 6px 18px rgba(0,0,0,0.22)',
              }}>
                <Sparkles size={14} fill="#ffffff" color="#ffffff" strokeWidth={2} /> Serious Job Seekers
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, marginTop: 8 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: '#273549', display: 'flex', alignItems: 'center', justifyContent: 'center', color: accentColor }}>
                <Crown size={20} strokeWidth={2} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 800, color: '#cbd5e1', letterSpacing: '0.12em' }}>PREMIUM</span>
            </div>

            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 22, fontWeight: 600, color: '#ffffff', marginRight: 2 }}>₹</span>
                  <span style={{ fontSize: 52, fontWeight: 900, color: '#ffffff', letterSpacing: '-0.03em' }}>{PREMIUM_PRICE}</span>
                </div>
                <span style={{ fontSize: 17, fontWeight: 600, color: '#64748b', textDecoration: 'line-through' }}>₹999</span>
                <span style={{ color: '#94a3b8', fontSize: 15, fontWeight: 600 }}>/ one-time</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#94a3b8', marginTop: 4, fontWeight: 500 }}>
                <TrendingDown size={16} color="#60a5fa" />
                <span style={{ fontSize: 13 }}><span style={{ color: '#93c5fd', fontWeight: 700 }}>Price dropped 65%</span></span>
              </div>
            </div>
            <p style={{ fontSize: 15, color: '#e2e8f0', marginBottom: 22, lineHeight: 1.58 }}>
              Apply with full confidence. Know exactly where you fit and who to talk to.
            </p>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 40 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <CheckCircle2 size={18} color={accentColor} strokeWidth={2.25} />
                <span style={{ fontSize: 15, color: '#ffffff', fontWeight: 700 }}>Everything in Free</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '4px 0' }}>
                <div style={{ height: 1, flex: 1, background: '#334155' }} />
                <span style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: '0.18em' }}>UNLOCKED</span>
                <div style={{ height: 1, flex: 1, background: '#334155' }} />
              </div>

              {premiumUnlockedItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <CheckCircle2 size={18} color={accentColor} style={{ marginTop: 2 }} strokeWidth={2.25} />
                  <span style={{ fontSize: 15, color: '#f8fafc', fontWeight: 500, lineHeight: 1.45 }}>{item}</span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {!user?.is_premium ? (
                <>
                  <CashfreeCheckoutButton
                    amountRupees={PREMIUM_PRICE}
                    buttonLabel="Unlock everything"
                    disabled={false}
                    className="premium-checkout-btn"
                    style={{ width: '100%', height: 56, borderRadius: 999, fontSize: 15 }}
                  />
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '18px', borderRadius: 999, background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.14)', color: accentColor, fontWeight: 800, fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                  <CheckCircle2 size={22} strokeWidth={2.25} /> Premium Active
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#64748b' }}>
                <ShieldCheck size={14} strokeWidth={2} />
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.02em' }}>One-time payment · 7-day refund · No subscription</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* The Math Section — no outer card; grid aligns with FAQ below */}
      <section style={{ maxWidth: CONTENT_MAX_WIDTH, margin: '0 auto', padding: `60px ${pxInset.r} 60px ${pxInset.l}` }}>
        <motion.div
          initial={{ opacity: 0, y: reduceMotion ? 0 : 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: reduceMotion ? 0.01 : 0.44, ease: easeOut }}
        >
          <div
            className="pricing-split-grid"
            style={{
              display: 'grid',
              gap: 'clamp(18px, 2.2vw, 26px)',
              alignItems: 'stretch',
            }}
          >
            {/* Lead copy — top-aligned with comparison cards */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignSelf: 'start', minWidth: 0 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 14, width: 'fit-content' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: accentColor }} />
                <p style={{ fontSize: 11, fontWeight: 800, color: accentColor, letterSpacing: '0.12em', textTransform: 'uppercase', margin: 0 }}>The math</p>
              </div>
              <h2 style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 'clamp(28px, 3.1vw, 40px)',
                fontWeight: 700,
                color: theme.textPrimary,
                lineHeight: 1.14,
                margin: '0 0 14px',
                letterSpacing: '-0.022em',
              }}>
                <span style={{ fontWeight: 800 }}>₹{PREMIUM_PRICE}</span>
                {' vs. the cost of '}
                <span style={{
                  fontFamily: "'Playfair Display', Georgia, 'Times New Roman', serif",
                  fontStyle: 'italic',
                  fontWeight: 600,
                  color: accentColor,
                  letterSpacing: '0.01em',
                }}>applying blind.</span>
              </h2>
              <p style={{ fontSize: 14, color: theme.textSecondary, lineHeight: 1.55, margin: '0 0 20px', maxWidth: 400, fontWeight: 400 }}>
                One right company and one strong referral can save months of job search. Cold applications cost months.
              </p>
              <div style={{
                display: 'inline-flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
                padding: '9px 13px',
                borderRadius: 100,
                background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                border: `1px solid ${theme.border}`,
                fontSize: 12,
                fontWeight: 600,
                color: theme.textMuted,
              }}>
                <span>Cost per resulting offer</span>
                <ArrowRight size={13} style={{ opacity: 0.45, flexShrink: 0 }} />
                <span style={{ color: accentColor, fontWeight: 800 }}>₹{PREMIUM_PRICE} once</span>
              </div>
            </div>

            {/* Applying Blind — same footprint as FairHire card */}
            <motion.div
              initial={{ opacity: 0, y: reduceMotion ? 0 : 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: reduceMotion ? 0.01 : 0.38, ease: easeOut }}
              className="pricing-math-stat-card"
              style={{
                background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                borderRadius: 24,
                padding: '28px 24px',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                boxShadow: isDark ? 'none' : '0 6px 20px rgba(15, 23, 42, 0.06)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%',
                minWidth: 0,
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                paddingBottom: 20,
                marginBottom: 8,
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
              }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#f87171', flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Applying blind</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {[
                  { label: 'Finding matching jobs in your network', value: '3+ wks' },
                  { label: 'Find companies with the best referral odds for you', value: '1+ wks' },
                  { label: 'Referral request replies (cold)', value: '4%' },
                  { label: 'Figuring out whom to message first', value: '6+ hrs' },
                  { label: 'Finding top companies hiring people like you', value: '3+ hrs' },
                ].map((row, i, arr) => (
                  <div
                    key={row.label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 12,
                      padding: '15px 0',
                      borderBottom: i < arr.length - 1 ? `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0'}` : 'none',
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1, paddingRight: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: theme.textSecondary, lineHeight: 1.4 }}>{row.label}</div>
                      {row.sub ? (
                        <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>{row.sub}</div>
                      ) : null}
                    </div>
                    <span style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: theme.textMuted,
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '-0.01em',
                      flexShrink: 0,
                      textAlign: 'right',
                      maxWidth: '42%',
                    }}>{row.value}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* With FairHire — hex-only inline colors: .light-app global CSS matches
                substring rgba(255,255,255,…) in style="" and forces dark text + blue
                backgrounds on rows; avoid those tokens here. */}
            <motion.div
              initial={{ opacity: 0, y: reduceMotion ? 0 : 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: reduceMotion ? 0.01 : 0.38, delay: reduceMotion ? 0 : 0.05, ease: easeOut }}
              className="pricing-math-fairhire-card pricing-math-stat-card"
              style={{
                background: isDark ? '#111827' : '#1e293b',
                borderRadius: 24,
                padding: '28px 24px',
                border: `1px solid ${isDark ? '#334155' : '#475569'}`,
                boxShadow: isDark ? '0 8px 28px rgba(0,0,0,0.35)' : '0 8px 26px rgba(15, 23, 42, 0.12)',
                display: 'flex',
                flexDirection: 'column',
                color: '#f8fafc',
                minHeight: 0,
                height: '100%',
                minWidth: 0,
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                paddingBottom: 20,
                marginBottom: 8,
                borderBottom: '1px solid #334155',
              }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: accentColor, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.12em', textTransform: 'uppercase' }}>With FairHire</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {[
                  { label: 'Finding matching jobs in your network', value: '~20 min' },
                  { label: 'Find companies with the best referral odds for you', value: '~5 min' },
                  { label: 'Referral request replies (guided)', value: '~15–20%' },
                  { label: 'Figuring out whom to message first', value: '~2 mins' },
                  { label: 'Finding top companies hiring people like you', value: '~5 min' },
                ].map((row, i, arr) => (
                  <div
                    key={row.label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 12,
                      padding: '15px 0',
                      borderBottom: i < arr.length - 1 ? '1px solid #334155' : 'none',
                      background: 'transparent',
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1, paddingRight: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: '#cbd5e1', lineHeight: 1.4 }}>{row.label}</div>
                      {row.sub ? (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{row.sub}</div>
                      ) : null}
                    </div>
                    <span style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#e2e8f0',
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '-0.01em',
                      flexShrink: 0,
                      textAlign: 'right',
                      maxWidth: '42%',
                    }}>{row.value}</span>
                  </div>
                ))}
              </div>
              <div style={{
                marginTop: 18,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '13px 16px',
                background: '#0f172a',
                borderRadius: 14,
                border: '1px solid #334155',
              }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>FairHire</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#f8fafc' }}>
                  ₹{PREMIUM_PRICE} <span style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8' }}>one-time</span>
                </span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* FAQ Section — same grid as math: lead column + wide panel = two card columns */}
      <section style={{ maxWidth: CONTENT_MAX_WIDTH, margin: '0 auto', padding: `60px ${pxInset.r} 60px ${pxInset.l}` }}>
        <div
          className="faq-section-layout pricing-split-grid"
          style={{
            display: 'grid',
            gap: 'clamp(18px, 2.2vw, 26px)',
            alignItems: 'start',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignSelf: 'start', minWidth: 0 }}>
            <p style={{ fontSize: 11, fontWeight: 800, color: accentColor, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 14px' }}>Questions, answered</p>
            <h2 style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 'clamp(28px, 3.1vw, 40px)',
              fontWeight: 700,
              margin: '0 0 14px',
              lineHeight: 1.14,
              letterSpacing: '-0.022em',
              color: theme.textPrimary,
            }}>
              Everything you&apos;d want to{' '}
              <span style={{
                fontFamily: "'Playfair Display', Georgia, 'Times New Roman', serif",
                fontStyle: 'italic',
                fontWeight: 600,
                color: accentColor,
                letterSpacing: '0.01em',
              }}>ask.</span>
            </h2>
            <p style={{ fontSize: 14, color: theme.textSecondary, lineHeight: 1.55, maxWidth: 400, margin: 0 }}>
              Still unsure? Email{' '}
              <a href="mailto:support@fairhire.in" style={{ color: accentColor, textDecoration: 'none', fontWeight: 600 }}>support@fairhire.in</a>
              {' '}and we&apos;ll reply within a day.
            </p>
          </div>
          <div
            className="faq-section-accordion"
            style={{
              background: theme.cardBg,
              padding: 'clamp(20px, 2.5vw, 28px)',
              borderRadius: 22,
              boxShadow: isDark ? 'none' : '0 6px 20px rgba(15, 23, 42, 0.06)',
              border: theme.cardBorder,
              minWidth: 0,
            }}
          >
            {faqItems.map((item, i) => (
              <AccordionItem
                key={i}
                question={item.q}
                answer={item.a}
                isOpen={openFaqIndex === i}
                onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                accentColor={accentColor}
                textPrimary={theme.textPrimary}
                textSecondary={theme.textSecondary}
                borderColor={isDark ? '#334155' : '#f1f5f9'}
                isLast={i === faqItems.length - 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section style={{ maxWidth: CONTENT_MAX_WIDTH, margin: '0 auto', padding: `32px ${pxInset.r} 48px ${pxInset.l}`, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <div style={{ height: 1, flex: 1, background: isDark ? '#334155' : 'rgba(37, 99, 235, 0.2)' }} />
          <span style={{ fontSize: 10, letterSpacing: '0.16em', fontWeight: 800, color: theme.textMuted }}>READY WHEN YOU ARE</span>
          <div style={{ height: 1, flex: 1, background: isDark ? '#334155' : 'rgba(37, 99, 235, 0.2)' }} />
        </div>
        <motion.div
          initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: reduceMotion ? 0.01 : 0.4, ease: easeOut }}
        >
          <h2 style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: 'clamp(24px, 3.8vw, 36px)',
            fontWeight: 800,
            margin: '0 0 12px',
            letterSpacing: '-0.02em',
            color: theme.textPrimary,
          }}>
            Stop applying blind.{' '}
            <span style={{ color: accentColor, fontWeight: 800 }}>Start applying smart.</span>
          </h2>
          <p style={{ color: theme.textSecondary, fontSize: 15, margin: '0 0 28px', lineHeight: 1.55 }}>
            One small payment. Every insight. No subscription.
          </p>

          {!user?.is_premium ? null : (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 22px',
              borderRadius: 999,
              background: isDark ? 'rgba(245,158,11,0.14)' : 'rgba(37,99,235,0.12)',
              color: accentColor,
              fontWeight: 700,
              fontSize: 15,
            }}>
              <CheckCircle2 size={18} strokeWidth={2.25} /> Premium Access Active
            </div>
          )}
        </motion.div>
      </section>
      <div style={{ height: 24 }} />

      <style>{`
        .pricing-split-grid {
          grid-template-columns: 1fr;
        }
        @media (min-width: 900px) {
          .pricing-split-grid {
            grid-template-columns: minmax(260px, 1.05fr) minmax(0, 1fr) minmax(0, 1fr);
          }
          .faq-section-accordion {
            grid-column: 2 / -1;
          }
        }
        .premium-checkout-btn, .bottom-cta-btn {
          background: #ffffff !important;
          color: #0f172a !important;
          border: none !important;
          font-weight: 900 !important;
          letter-spacing: 0.01em !important;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2) !important;
          transition: transform 0.2s ease, box-shadow 0.2s ease !important;
        }
        .premium-checkout-btn:hover, .bottom-cta-btn:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 15px 40px rgba(0,0,0,0.3) !important;
          background: #f8fafc !important;
        }
        .premium-checkout-btn:active, .bottom-cta-btn:active {
          transform: translateY(0) !important;
        }
        @media (prefers-reduced-motion: reduce) {
          .premium-checkout-btn:hover, .bottom-cta-btn:hover {
            transform: none !important;
          }
        }
      `}</style>
    </div>
  );
};


export default PricingPage;
